<template>
  <div class="acg-page">
    <!-- ═══ 表單模式 ═══ -->
    <div v-if="showForm">
      <div class="page-header custom-container">
        <span>ACG 幸運地圖</span>
      </div>
      <div class="member custom-container-xl">
        <div class="member__box">
          <p class="acg-intro">
            根據您的出生資料，在全球地圖上畫出 10 顆行星的「幸運能量線」。<br />
            住在某條線上，代表該行星能量在當地得到強化。
          </p>
          <div class="personal__info">
            <!-- 出生日期 -->
            <div class="personal__item">
              <span class="item__title">出生日期</span>
              <div class="item__input" @click.stop="dateModal = true">
                <span>{{ birthDate }}</span>
                <v-dialog
                  ref="dialogDate"
                  v-model="dateModal"
                  :return-value.sync="birthDate"
                  width="290px"
                  persistent
                >
                  <v-date-picker
                    class="picker date-picker"
                    v-model="birthDate"
                    :first-day-of-week="0"
                    locale="zh-cn"
                    scrollable
                    header-color="rgba(217, 217, 217, 0.25)"
                    color="primary"
                    max="2099-12-31"
                    min="0676-01-01"
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="error" @click="dateModal = false"
                      >取消</v-btn
                    >
                    <v-btn
                      text
                      color="error"
                      @click="$refs.dialogDate.save(birthDate)"
                      >確認</v-btn
                    >
                  </v-date-picker>
                </v-dialog>
              </div>
            </div>

            <!-- 出生時間 -->
            <div class="personal__item">
              <span class="item__title">出生時間</span>
              <div class="time-wrap">
                <div class="item__input" @click.stop="timeModal = true">
                  <span>{{ birthTime }}</span>
                  <v-dialog
                    ref="dialogTime"
                    v-model="timeModal"
                    :return-value.sync="birthTime"
                    width="290px"
                    persistent
                  >
                    <v-time-picker
                      class="picker time-picker"
                      v-model="birthTime"
                      v-if="timeModal"
                      scrollable
                      ampm-in-title
                      header-color="rgba(217, 217, 217, 0.25)"
                      color="primary"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="error" @click="timeModal = false"
                        >取消</v-btn
                      >
                      <v-btn
                        text
                        color="error"
                        @click="$refs.dialogTime.save(birthTime)"
                        >確認</v-btn
                      >
                    </v-time-picker>
                  </v-dialog>
                </div>
                <label class="checkbox-wrap">
                  我不知道
                  <input type="checkbox" v-model="IDontKnow" />
                  <span class="checkmark"></span>
                </label>
              </div>
            </div>

            <!-- 日光節約時間 -->
            <div class="personal__item">
              <span class="item__title">日光節約時間</span>
              <div class="item__sex">
                <div
                  class="sex-box male"
                  :class="{ activeSex: IsDaylight }"
                  @click="IsDaylight = true"
                >
                  是
                </div>
                <div
                  class="sex-box female"
                  :class="{ activeSex: !IsDaylight }"
                  @click="IsDaylight = false"
                >
                  否
                </div>
              </div>
            </div>

            <!-- 出生地點（用於判斷出生時區） -->
            <div class="personal__item">
              <span class="item__title">出生地點</span>
              <div class="item__input">
                <v-autocomplete
                  class="select"
                  :items="cityList"
                  v-model="birthCity"
                  label="請選擇或輸入出生地點"
                  background-color="transparent"
                  solo
                  hide-details
                ></v-autocomplete>
              </div>
            </div>
          </div>

          <div class="personal__btns">
            <div
              class="personal__save"
              :class="{ notReady: !formReady }"
              @click="calculate"
            >
              生成幸運地圖
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══ 結果模式 ═══ -->
    <div v-else class="result-wrap custom-container">
      <!-- 頂部操作列 -->
      <div class="result-header">
        <div class="personal__cancel" @click="backToForm">← 返回設定</div>
        <div class="birth-info-tag">{{ formattedBirthDateTime }}</div>
      </div>

      <div class="result-body">
        <!-- 左側：圖例 -->
        <div class="legend-panel">
          <div class="legend-title">行星能量線</div>

          <!-- 行星篩選 -->
          <div class="legend-planets">
            <div
              v-for="p in planetList"
              :key="p.key"
              class="legend-item"
              @click="togglePlanet(p.key)"
            >
              <span
                class="legend-dot"
                :style="{
                  background: p.color,
                  opacity: visiblePlanets[p.key] ? 1 : 0.2,
                }"
              ></span>
              <span
                class="legend-name"
                :class="{ dimmed: !visiblePlanets[p.key] }"
                >{{ p.zh }}</span
              >
            </div>
          </div>

          <!-- 線型說明 -->
          <div class="legend-angles">
            <div class="legend-subtitle">線型說明</div>
            <div class="legend-angle-item">
              <span class="line-sample solid"></span> MC（中天）
            </div>
            <div class="legend-angle-item">
              <span class="line-sample dashed"></span> IC（天底）
            </div>
            <div class="legend-angle-item">
              <span class="line-sample dotted"></span> ASC（上升）
            </div>
            <div class="legend-angle-item">
              <span class="line-sample dash-dot"></span> DSC（下降）
            </div>
          </div>

          <!-- 點擊地點資訊 -->
          <div class="location-info" v-if="clickedLocation">
            <div class="legend-subtitle">點擊位置</div>
            <div class="location-coords">
              {{ formatLat(clickedLocation.lat) }},
              {{ formatLng(clickedLocation.lng) }}
            </div>
            <div class="location-city" v-if="geocodingLoading">
              <span class="geocoding-hint">查詢地名中…</span>
            </div>
            <div class="location-city" v-else-if="nearestCity">
              地點：<strong>{{ nearestCity }}</strong>
            </div>
            <div class="location-tz" v-if="clickedTzDisplay">
              法定時區：<strong>{{ clickedTzDisplay }}</strong>
            </div>
            <div class="location-btns">
              <div
                class="location-btn"
                :class="{ loading: relocationLoading }"
                @click="loadRelocation"
              >
                {{ relocationLoading ? '計算中…' : '查看重置盤' }}
              </div>
            </div>
          </div>
        </div>

        <!-- 右側：地圖 -->
        <div class="map-container">
          <!-- loading 遠罩，覆蓋地圖上方；leaflet 一直保持在 DOM 中 -->
          <div v-if="calculating" class="map-loading">
            <div class="loading-spinner"></div>
            <span>計算行星軌跡中…</span>
          </div>
          <!-- GPS 定位按鈕 -->
          <button
            class="locate-btn"
            :class="{ locating }"
            @click.stop="locateMe"
            :title="locating ? '定位中…' : '定位我的位置'"
          >
            <svg v-if="!locating" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
              <circle cx="12" cy="12" r="8" stroke-opacity="0.4"/>
            </svg>
            <span v-else class="locate-spinner"></span>
          </button>

          <l-map
            ref="leafletMap"
            :zoom="zoom"
            :center="center"
            :options="mapOptions"
            class="leaflet-map"
            @click="onMapClick"
            @ready="onMapReady"
            @update:zoom="zoom = $event"
          >
            <l-tile-layer :url="tileUrl" :attribution="tileAttribution" :options="tileOptions" />

            <!-- 城市定點（zoom ≥ 4 後顯示，方便點擊） -->
            <template v-if="zoom >= 4">
              <l-circle-marker
                v-for="city in cityDotList"
                :key="city.name"
                :lat-lng="[city.lat, city.lng]"
                :radius="6"
                color="#fff"
                fill-color="#ff8c42"
                :fill-opacity="0.9"
                :weight="1.5"
                class="city-dot"
                @click.stop="onCityClick(city)"
              >
                <l-tooltip :options="{ direction: 'top', sticky: false, className: 'city-tooltip' }">
                  {{ city.name }}
                </l-tooltip>
              </l-circle-marker>
            </template>

            <!-- 各行星各尖軸的線條 -->
            <template v-for="feature in filteredFeatures">
              <l-polyline
                :key="feature._id"
                :lat-lngs="featureLatLngs(feature)"
                :color="feature.properties.color"
                :weight="lineWeight(feature.properties.angle)"
                :dash-array="lineDash(feature.properties.angle)"
                :opacity="0.85"
              >
                <l-tooltip :options="{ sticky: true, direction: 'top' }">
                  {{ feature.properties.label }}
                </l-tooltip>
              </l-polyline>
            </template>

            <!-- 點擊標記 -->
            <l-circle-marker
              v-if="clickedLocation"
              :lat-lng="[clickedLocation.lat, clickedLocation.lng]"
              :radius="7"
              color="#fff"
              fill-color="#ee6daf"
              :fill-opacity="1"
              :weight="2"
            />
          </l-map>
        </div>
      </div>

      <!-- ═══ 重置盤結果 ═══ -->
      <div class="relocation-result" v-if="relocationAstralInfo">
        <div class="relocation-result__header">
          <div class="relocation-result__title">重置盤</div>
          <div class="relocation-result__info">
            {{ formattedBirthDateTime }}
            <span v-if="nearestCity">&nbsp;·&nbsp;{{ nearestCity }}</span>
            <span v-if="clickedTzDisplay">&nbsp;{{ clickedTzDisplay }}</span>
          </div>
        </div>
        <div class="relocation-result__body">
          <div class="relocation-params">
            <Params
              :TcAstralInfo="relocationAstralInfo"
              :AstrolabeType="'C2'"
              role="Relocation"
              :moondayObj="relocationMoondayObj"
            />
          </div>
          <NewAstrolabe
            :data1="relocationAstralInfo.AstralRadians"
            :data2="null"
            :type="0"
            :styleArray="relocationStyleArray"
            role="Relocation"
            :Ret1="relocationAstralInfo.RetAstralSet"
            :showNowTime="false"
            :format_time="formattedBirthDateTime"
            mode="astrometryMode"
            :nowTimeNowPlaceBtn="false"
            :nowTimeNowPlaceIng="false"
            :nowPlace="clickedPlaceDisplay"
            :moondayObj="relocationMoondayObj"
            :hiddenuserDesktopInfo="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  LMap,
  LTileLayer,
  LPolyline,
  LTooltip,
  LCircleMarker,
} from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import AMapLoader from "@amap/amap-jsapi-loader";
import {
  computeAcgFromBirth,
  PLANET_COLORS,
  PLANET_ZH,
  PLANETS,
} from "@/js/acgEngine.js";
import cityCoordinates from "@/js/cityCoordinates.js";
import tzlookup from "tz-lookup";
import { getTzOffset, formatTzOffset } from "@/js/tzUtils.js";
import NewAstrolabe from "@/components/星盤組件/new_astrolabe";
import Params from "@/components/星盤組件/Params_astrometry.vue";

// ─── 高德地圖 API 金鑰設定 ──────────────────────────────────────────────────
// 請至 https://console.amap.com/dev/key/app 建立「Web端（JS API）」應用
// 取得 API Key 與安全密鑰後填入下方
const AMAP_KEY           = "2b778027d2e490b81eda98c043035e82"; // JS API Key（必填）
const AMAP_SECURITY_CODE = "c4f251702c50784bba8b3b5e3534ee75"; // 安全密鑰 securityJsCode（必填）

const LS_KEY = "acg_form";

export default {
  name: "AcgMapPage",
  components: { LMap, LTileLayer, LPolyline, LTooltip, LCircleMarker, NewAstrolabe, Params },

  data() {
    return {
      showForm: true,
      calculating: false,

      // 表單
      birthDate: "1990-01-01",
      birthTime: "12:00",
      IsDaylight: false,
      IDontKnow: false,
      dateModal: false,
      timeModal: false,

      // 地圖（僅顯示中國與台灣地區，隱形結界：限制縮放級別 + 拖曳邊界）
      zoom: 5,
      center: [35.86166, 104.195397], // 中國大陸中心點
      tileUrl: "https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=8&x={x}&y={y}&z={z}",
      tileOptions: { subdomains: ["1", "2", "3", "4"], maxZoom: 18, detectRetina: true },
      tileAttribution:
        '&copy; <a href="https://www.amap.com/" target="_blank">高德地圖</a>',
      mapOptions: {
        worldCopyJump: false,
        minZoom: 4,   // 最小縮放：無法再拉遠看到其他國家/大洲
        maxZoom: 18,
        // 拖曳邊界：[西南角(lat,lng), 東北角(lat,lng)]，涵蓋中國與台灣地區
        // 西南角約在帕米爾高原至南沙群島，東北角約在黑龍江至烏蘇里江交匯處
        maxBounds: [
          [3.8, 73.4],
          [53.5, 135.1],
        ],
        maxBoundsViscosity: 1.0, // 拖曳到邊界時強制彈回，無法離開範圍
      },

      // 高德 Geocoder 實例
      AMap: null,

      // ACG 計算結果
      geoJson: null,

      // 行星可見性
      visiblePlanets: Object.fromEntries(PLANETS.map((p) => [p, true])),

      // 出生地點
      birthCity: null,

      // 點擊資訊
      clickedLocation: null,
      nearestCity: null,
      clickedTzName: null,
      clickedTzOffset: null,

      // 重置盤結果
      relocationAstralInfo: null,
      relocationLoading: false,
      relocationStyleArray: [
        { name: "文字", displayName: "文字", show: false, id: "alert1" },
        { name: "JOE2", displayName: "專業", show: false, id: "alert5" },
      ],
      relocationMoondayObj: {},
      // 點擊位置的地名查詢狀態
      geocodingLoading: false,
      // GPS 定位狀態
      locating: false,
    };
  },

  computed: {
    formReady() {
      return !!this.birthDate && !!this.birthTime && !!this.birthCity;
    },
    formattedBirthDateTime() {
      if (!this.birthDate || !this.birthTime) return "";
      return `${this.birthDate.replace(/-/g, "/")} ${this.birthTime}`;
    },
    cityList() {
      return Object.keys(cityCoordinates);
    },
    // 出生地的真實法定時區（以小時計）
    birthTzOffset() {
      if (!this.birthCity) return 8;
      const coords = cityCoordinates[this.birthCity];
      if (!coords) return 8;
      try {
        const tzName = tzlookup(parseFloat(coords.lat), parseFloat(coords.lng));
        return getTzOffset(tzName);
      } catch (_) {
        return 8;
      }
    },
    // 點擊地點的時區顯示字串
    clickedTzDisplay() {
      if (this.clickedTzOffset == null) return "";
      return formatTzOffset(this.clickedTzOffset);
    },
    planetList() {
      return PLANETS.map((key) => ({
        key,
        zh: PLANET_ZH[key],
        color: PLANET_COLORS[key],
      }));
    },
    cityDotList() {
      return Object.entries(cityCoordinates).map(([name, coords]) => ({
        name,
        lat: parseFloat(coords.lat),
        lng: parseFloat(coords.lng),
      }));
    },
    filteredFeatures() {
      if (!this.geoJson) return [];
      return this.geoJson.features
        .filter((f) => this.visiblePlanets[f.properties.planet])
        .map((f, i) => ({ ...f, _id: i }));
    },
    // 點擊地點的 lat/lng 轉成 API 格式
    clickedLatFormatted() {
      if (!this.clickedLocation) return null;
      const v = this.clickedLocation.lat;
      return v >= 0
        ? parseFloat(v).toFixed(2) + "N"
        : Math.abs(parseFloat(v)).toFixed(2) + "S";
    },
    clickedLngFormatted() {
      if (!this.clickedLocation) return null;
      const v = this.clickedLocation.lng;
      return v >= 0
        ? parseFloat(v).toFixed(2) + "E"
        : Math.abs(parseFloat(v)).toFixed(2) + "W";
    },
    clickedPlaceDisplay() {
      if (!this.clickedLngFormatted || !this.clickedLatFormatted) return "";
      return `${this.clickedLngFormatted}, ${this.clickedLatFormatted}`;
    },
    relocationArg() {
      if (!this.clickedLocation) return null;
      return {
        AstrolabeType: "SC",
        BirthTime: this.formattedBirthDateTime,
        IsDaylight: this.IsDaylight,
        BirthLocation: {
          TimeZone: this.clickedTzOffset != null ? this.clickedTzOffset : 8,
          Latitude: this.clickedLatFormatted,
          Longitude: this.clickedLngFormatted,
        },
      };
    },
  },

  watch: {
    IDontKnow(val) {
      if (val) this.birthTime = "12:00";
    },
  },

  created() {
    this.loadFromStorage();
    this.initAmapGeocoder();
  },

  methods: {
    // ─── GPS 定位 ────────────────────────────────────────────────────────────────
    locateMe() {
      if (this.locating) return;
      this.locating = true;

      const onSuccess = (lat, lng) => {
        this.locating = false;
        // 設定點擊位置並將地圖移至該處
        this.onMapClick({ latlng: { lat, lng } });
        if (this.$refs.leafletMap && this.$refs.leafletMap.mapObject) {
          this.$refs.leafletMap.mapObject.setView([lat, lng], Math.max(this.zoom, 6));
        }
      };

      const onError = (msg) => {
        this.locating = false;
        console.warn('[ACG] 定位失敗', msg);
        alert('定位失敗，請確認瀏覽器已允許位置權限');
      };

      // 唒先嘗試 AMap.Geolocation（大陸精確 IP 定位 + 瀏覽器 GPS）
      if (this.AMap && this.AMap.Geolocation) {
        const geo = new this.AMap.Geolocation({
          enableHighAccuracy: true,
          timeout: 10000,
          convert: true, // 自動偵點坐標側偏
          GeoLocationFirst: true,
        });
        geo.getCurrentPosition((status, result) => {
          if (status === 'complete') {
            const pos = result.position;
            onSuccess(pos.getLat(), pos.getLng());
          } else {
            // 高德失敗後 fallback 瀏覽器原生定位
            this._browserGeolocate(onSuccess, onError);
          }
        });
      } else {
        // 金鑰未設定時直接用瀏覽器原生定位
        this._browserGeolocate(onSuccess, onError);
      }
    },
    _browserGeolocate(onSuccess, onError) {
      if (!navigator.geolocation) {
        onError('此瀏覽器不支援 Geolocation');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => onSuccess(pos.coords.latitude, pos.coords.longitude),
        (err) => onError(err.message),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    },

    // ─── 城市定點點擊 ───────────────────────────────────────────────────────────
    onCityClick(city) {
      this.clickedLocation = { lat: city.lat, lng: city.lng };
      this.relocationAstralInfo = null;
      this.nearestCity = city.name; // 直接使用已知城市名稱，無需 geocoding
      this.geocodingLoading = false;
      try {
        const tzName = tzlookup(city.lat, city.lng);
        this.clickedTzName = tzName;
        this.clickedTzOffset = getTzOffset(tzName);
      } catch (_) {
        this.clickedTzName = null;
        this.clickedTzOffset = null;
      }
    },

    // ─── 高德 Geocoder 初始化 ───────────────────────────────────────────────
    initAmapGeocoder() {
      if (!AMAP_KEY || !AMAP_SECURITY_CODE) return; // 金鑰未設定則跳過
      try {
        window._AMapSecurityConfig = { securityJsCode: AMAP_SECURITY_CODE };
        AMapLoader.load({
          key: AMAP_KEY,
          version: "2.0",
          plugins: ["AMap.Geocoder", "AMap.Geolocation"],
        }).then((AMap) => {
          this.AMap = AMap;
        }).catch((err) => {
          console.warn("[ACG] 高德地圖 API 載入失敗", err);
        });
      } catch (err) {
        console.warn("[ACG] 高德地圖 API 初始化失敗", err);
      }
    },

    // ─── 計算 ────────────────────────────────────────────────────────────────
    calculate() {
      if (!this.formReady) return;
      this.calculating = true;
      this.showForm = false;
      this.geoJson = null;
      this.clickedLocation = null;
      this.nearestCity = null;

      // 讓 UI 先切換到結果模式再做計算（避免阻塞渲染）
      this.$nextTick(() => {
        setTimeout(() => {
          try {
            this.geoJson = computeAcgFromBirth(
              this.birthDate,
              this.birthTime,
              this.IsDaylight,
              this.birthTzOffset // 使用真實法定時區
            );
          } catch (e) {
            console.error("ACG 計算失敗", e);
          }
          this.calculating = false;
          this.saveToStorage();
          // 地圖一直在 DOM 中，計算完成後強制刷新尺寸
          this.$nextTick(() => {
            if (this.$refs.leafletMap && this.$refs.leafletMap.mapObject) {
              this.$refs.leafletMap.mapObject.invalidateSize();
            }
            // 觸發 window resize 作為最後的保險（手機端有效）
            window.dispatchEvent(new Event("resize"));
          });
        }, 50);
      });
    },

    backToForm() {
      this.showForm = true;
    },

    // l-map 就緒時立即修正尺寸（v-else 切換後 Leaflet 可能計算到 0 高度）
    onMapReady(mapObject) {
      this.$nextTick(() => {
        mapObject.invalidateSize();
      });
    },

    // ─── 地圖事件 ────────────────────────────────────────────────────────────
    async onMapClick(e) {
      const { lat, lng } = e.latlng;
      this.clickedLocation = { lat, lng };
      this.relocationAstralInfo = null;
      this.nearestCity = null;

      // 真實法定時區查詢
      try {
        const tzName = tzlookup(lat, lng);
        this.clickedTzName = tzName;
        this.clickedTzOffset = getTzOffset(tzName);
      } catch (_) {
        this.clickedTzName = null;
        this.clickedTzOffset = null;
      }

      // 高德地圖逆地址解析（取代 Nominatim，可在大陸正常使用）
      this.geocodingLoading = true;
      if (this.AMap) {
        try {
          const geocoder = new this.AMap.Geocoder({ radius: 1000 });
          geocoder.getAddress([lng, lat], (status, result) => {
            this.geocodingLoading = false;
            if (status === "complete" && result.info === "OK") {
              const a = result.regeocode.addressComponent;
              // 依行政層級從精細到粗略依序取值
              this.nearestCity =
                a.city || a.district || a.township ||
                a.suburb || result.regeocode.formattedAddress || "";
            }
          });
        } catch (_) {
          this.geocodingLoading = false;
        }
      } else {
        // 金鑰未設定或 API 尚未載入時，靜默略過
        this.geocodingLoading = false;
      }
    },

    featureLatLngs(feature) {
      return feature.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
    },

    lineWeight(angle) {
      return angle === "MC" || angle === "IC" ? 2.5 : 2;
    },

    lineDash(angle) {
      if (angle === "IC") return "8 4";
      if (angle === "ASC") return "3 4";
      if (angle === "DSC") return "8 3 2 3";
      return null; // MC 實線
    },

    // ─── 最近城市查找 ─────────────────────────────────────────────────────────
    findNearestCity(lat, lng) {
      let best = null,
        bestDist = Infinity;
      for (const city of this.cityEntries) {
        const dlat = city.lat - lat;
        const dlng = city.lng - lng;
        const dist = dlat * dlat + dlng * dlng;
        if (dist < bestDist) {
          bestDist = dist;
          best = city.name;
        }
      }
      return best;
    },

    // ─── 格式化 ──────────────────────────────────────────────────────────────
    formatLat(v) {
      return v >= 0 ? `${v.toFixed(2)}°N` : `${Math.abs(v).toFixed(2)}°S`;
    },
    formatLng(v) {
      return v >= 0 ? `${v.toFixed(2)}°E` : `${Math.abs(v).toFixed(2)}°W`;
    },

    // ─── 行星篩選 ────────────────────────────────────────────────────────────
    togglePlanet(key) {
      this.$set(this.visiblePlanets, key, !this.visiblePlanets[key]);
    },

    // ─── 在頁面內加載重置盤 ──────────────────────────────────────────────────────
    loadRelocation() {
      if (!this.relocationArg) return;
      this.relocationLoading = true;
      this.relocationAstralInfo = null;
      this.$API.POST_TcAstralInfo(this.relocationArg).then((res) => {
        this.relocationLoading = false;
        if (res && res.Data && res.Data.Result) {
          this.relocationAstralInfo = res.Data.AstralInfo;
          this.$nextTick(() => {
            const el = this.$el.querySelector(".relocation-result");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        } else {
          alert("重置盤計算失敗，請確認輸入資料後重試");
        }
      }).catch(() => {
        this.relocationLoading = false;
        alert("重置盤計算失敗，請稍後再試");
      });
    },

    // ─── LocalStorage ────────────────────────────────────────────────────────
    loadFromStorage() {
      try {
        const raw = window.localStorage.getItem(LS_KEY);
        if (!raw) return;
        const data = JSON.parse(raw);
        this.birthDate = data.birthDate || this.birthDate;
        this.birthTime = data.birthTime || this.birthTime;
        this.IsDaylight = !!data.IsDaylight;
        this.IDontKnow = !!data.IDontKnow;
        this.birthCity = data.birthCity || null;
      } catch (_) {
        /* ignore */
      }
    },

    saveToStorage() {
      try {
        window.localStorage.setItem(
          LS_KEY,
          JSON.stringify({
            birthDate: this.birthDate,
            birthTime: this.birthTime,
            IsDaylight: this.IsDaylight,
            IDontKnow: this.IDontKnow,
            birthCity: this.birthCity,
          })
        );
      } catch (_) {
        /* ignore */
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_mixin.scss";

.acg-page {
  min-height: 100vh;
  background-color: $theme-color-3;
  color: $secondary-color-1;
}

// ─── 表單區（沿用 重置盤/editFile 樣式） ─────────────────────────────────────
@import "@/scss/header.scss";
@import "@/scss/userFile.scss";
@import "@/scss/editFile.scss";

.acg-intro {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.8;
  margin-bottom: 24px;
  letter-spacing: 0.5px;
  padding-top: 36px;
  @include mobile {
    font-size: 13px;
    padding-top: 24px;
  }
}

.personal__btns .personal__save {
  width: 100%;
}

// ─── 結果頁面 ─────────────────────────────────────────────────────────────────
.result-wrap {
  padding-top: 20px;
  padding-bottom: 40px;
}

.result-header {
  @include align(space-between, center);
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;

  .personal__cancel {
    cursor: pointer;
    color: $theme-color-1;
    font-size: 14px;
    padding: 6px 0;
    transition: opacity 0.2s;
    &:hover {
      opacity: 0.75;
    }
  }

  .birth-info-tag {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
    padding: 4px 12px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 20px;
  }
}

.result-body {
  @include align(flex-start, flex-start);
  gap: 20px;

  @include laptop {
    flex-direction: column;
  }
}

// ─── 圖例面板 ────────────────────────────────────────────────────────────────
.legend-panel {
  width: 200px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;

  @include laptop {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @include pad {
    grid-template-columns: 1fr;
  }
}

.legend-title {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 14px;
  letter-spacing: 0.5px;

  @include laptop {
    grid-column: 1 / -1;
    margin-bottom: 0;
  }
}

.legend-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.legend-planets {
  margin-bottom: 20px;
}

.legend-item {
  @include align(flex-start, center);
  gap: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.85;
  }
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: opacity 0.2s;
}

.legend-name {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  transition: color 0.2s;
  &.dimmed {
    color: rgba(255, 255, 255, 0.3);
  }
}

.legend-angles {
  margin-bottom: 20px;
}

.legend-angle-item {
  @include align(flex-start, center);
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 6px;
}

.line-sample {
  display: inline-block;
  width: 28px;
  height: 2px;
  background: rgba(255, 255, 255, 0.6);

  &.dashed {
    background: repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.6) 0,
      rgba(255, 255, 255, 0.6) 8px,
      transparent 8px,
      transparent 12px
    );
  }
  &.dotted {
    background: repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.6) 0,
      rgba(255, 255, 255, 0.6) 3px,
      transparent 3px,
      transparent 7px
    );
  }
  &.dash-dot {
    background: repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.6) 0,
      rgba(255, 255, 255, 0.6) 8px,
      transparent 8px,
      transparent 11px,
      rgba(255, 255, 255, 0.6) 11px,
      rgba(255, 255, 255, 0.6) 13px,
      transparent 13px,
      transparent 16px
    );
  }
}

// ─── 點擊位置資訊 ────────────────────────────────────────────────────────────
.location-info {
  margin-top: 4px;
}

.location-coords {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;
  font-family: monospace;

  .coords-tz {
    color: $theme-color-2;
    font-family: $tw_font;
  }
}

.location-city {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 6px;
  strong {
    color: #fff;
  }
}

.geocoding-hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
}

.location-tz {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 10px;
  strong {
    color: $theme-color-2;
  }
}

.location-btn {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid $theme-color-1;
  color: $theme-color-1;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: rgba($theme-color-1, 0.15);
  }
  &.loading {
    opacity: 0.6;
    cursor: default;
    pointer-events: none;
  }
}

// ─── 地圖容器 ────────────────────────────────────────────────────────────────
.map-container {
  flex: 1;
  min-width: 0;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  height: 560px;

  @include laptop {
    flex: none;   // column 方向時停用 flex-basis:0，讓明確 height 生效
    width: 100%;
    height: 480px;
  }
  @include pad {
    height: 420px;
  }
  @include mobile {
    height: 320px;
  }
}

.leaflet-map {
  width: 100%;
  height: 100%;
  background: #1a2035;
}

// ─── 定位按鈕 ─────────────────────────────────────────────────────────────────
.locate-btn {
  position: absolute;
  bottom: 32px;
  right: 12px;
  z-index: 1000;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(25, 17, 43, 0.88);
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  @include center;
  transition: all 0.2s;
  padding: 0;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: rgba(25, 17, 43, 0.98);
    color: $theme-color-1;
    border-color: $theme-color-1;
  }

  &.locating {
    cursor: default;
    opacity: 0.7;
  }
}

.locate-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: $theme-color-1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

// ─── Loading（絕對定位遮罩，不佔用地圖空間） ────────────────────────────────
.map-loading {
  @include center;
  flex-direction: column;
  gap: 14px;
  position: absolute;
  inset: 0;
  z-index: 1000;
  background: $theme-color-3;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-top-color: $theme-color-1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// ─── 覆寫 Leaflet 深色主題 ────────────────────────────────────────────────────
:deep(.leaflet-container) {
  background: #1a2035;
  font-family: $tw_font;
}

:deep(.leaflet-tooltip) {
  background: rgba(25, 17, 43, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

:deep(.leaflet-tooltip::before) {
  display: none;
}

// ─── 城市定點 tooltip ─────────────────────────────────────────────────────────
:deep(.city-tooltip) {
  background: rgba(25, 17, 43, 0.88);
  border: 1px solid rgba(255, 140, 66, 0.5);
  color: #ff8c42;
  font-size: 11px;
  padding: 3px 7px;
  border-radius: 4px;
  white-space: nowrap;
  &::before { display: none; }
}

// ─── 重置盤結果區 ──────────────────────────────────────────────────────────────
.relocation-result {
  margin-top: 40px;
  padding-top: 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  &__header {
    @include align(space-between, baseline);
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 24px;
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    letter-spacing: 0.5px;
  }

  &__info {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
  }

  &__body {
    @include align(space-around, flex-start);
    gap: 24px;

    @include laptop {
      flex-direction: column;
      align-items: center;
    }
  }
}

.relocation-params {
  width: 44%;
  flex-shrink: 0;

  @include laptop { width: 80%; }
  @include pad    { width: 100%; padding: 0 16px; }
  @include mobile { padding: 0 12px; }
}
</style>
